import { useState } from "react";
import { createPortal } from "react-dom";
import { Chip, Icon, Button } from "@legal-500/legal500-design-system";
import { useSearchableSelect } from "./useSearchableSelect";
import type { SelectOption } from "./useSearchableSelect";
import styles from "./SearchableSelect.module.css";

type SearchableMultiSelectProps = {
  name: string;
  label: string;
  required?: boolean;
  value: number[];
  options: SelectOption[];
  onChange: (value: number[]) => void;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
  error?: string;
  maxSelections?: number;
};

export function SearchableMultiSelect({
  name,
  label,
  required = false,
  value,
  options,
  onChange,
  disabled = false,
  placeholder = "Select options...",
  helperText,
  error,
  maxSelections,
}: SearchableMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);

  const {
    searchQuery,
    filteredOptions,
    dropdownStyle,
    containerRef,
    dropdownRef,
    searchInputRef,
    handleSearchChange,
  } = useSearchableSelect({
    options,
    isOpen,
    onClose: () => setIsOpen(false),
  });

  const selectedOptions = options.filter(opt => value.includes(opt.value as number));
  const isMaxReached = maxSelections !== undefined && value.length >= maxSelections;

  const handleToggle = (option: SelectOption) => {
    const optionValue = option.value as number;
    const isSelected = value.includes(optionValue);

    if (isSelected) {
      onChange(value.filter(v => v !== optionValue));
    } else if (!isMaxReached) {
      onChange([...value, optionValue]);
    }
  };

  const handleRemoveTag = (optionValue: number) => {
    onChange(value.filter(v => v !== optionValue));
  };

  const errorId = `${name}-error`;
  const helperId = `${name}-helper`;
  const describedBy = error ? errorId : helperText ? helperId : undefined;

  return (
    <div className={styles.formField}>
      <label htmlFor={name} className={styles.label}>
        {label}{" "}
        {required && (
          <span className={styles.required} aria-label="required">
            *
          </span>
        )}
      </label>

      <div className={styles.selectWrapper}>
        <div ref={containerRef} className={styles.selectContainer}>
          <button
            type="button"
            id={name}
            className={styles.selectButton}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-describedby={describedBy}
            aria-invalid={!!error}
          >
            <div className={styles.selectedTags}>
              {selectedOptions.length === 0 ? (
                <span className={styles.placeholder}>{placeholder}</span>
              ) : (
                selectedOptions.map(option => (
                  <Chip
                    key={option.value}
                    size="small"
                    variant="outlined"
                    onDelete={() => handleRemoveTag(option.value as number)}
                  >
                    {option.label}
                  </Chip>
                ))
              )}
            </div>
            <Icon
              icon={isOpen ? "chevron-up" : "chevron-down"}
              size="small"
              className={styles.arrow}
            />
          </button>
        </div>

        {isOpen &&
          createPortal(
            <div ref={dropdownRef} className={styles.dropdown} style={dropdownStyle}>
              <div className={styles.searchContainer}>
                <input
                  ref={searchInputRef}
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={e => handleSearchChange(e.target.value)}
                />
              </div>

              {maxSelections && (
                <div className={styles.selectionCounter}>
                  Selected: {value.length} / {maxSelections}
                </div>
              )}

              <ul className={styles.optionsList} role="listbox" aria-multiselectable="true">
                {filteredOptions.length === 0 ? (
                  <li className={styles.noResults}>No results found</li>
                ) : (
                  filteredOptions.map(option => {
                    const isSelected = value.includes(option.value as number);
                    const isDisabled = !isSelected && isMaxReached;

                    return (
                      <li
                        key={String(option.value)}
                        className={`${styles.option} ${
                          isSelected ? styles.optionSelected : ""
                        } ${isDisabled ? styles.optionDisabled : ""}`}
                        onClick={() => !isDisabled && handleToggle(option)}
                        role="option"
                        aria-selected={isSelected}
                        aria-disabled={isDisabled}
                      >
                        <div className={styles.optionContent}>
                          <span className={styles.optionLabel}>{option.label}</span>
                          {option.description && (
                            <span className={styles.optionDescription}>{option.description}</span>
                          )}
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>,
            document.body,
          )}

        {selectedOptions.length > 0 && (
          <Button
            onClick={() => onChange([])}
            disabled={disabled}
            size="small"
            variant="text"
            aria-label="Clear all selections"
          >
            Clear all
          </Button>
        )}
      </div>

      {error && (
        <span id={errorId} className={styles.errorText} role="alert" aria-live="polite">
          {error}
        </span>
      )}
      {helperText && !error && (
        <span id={helperId} className={styles.helperText}>
          {helperText}
        </span>
      )}
    </div>
  );
}
